import loginDefaultBg from "@/assest/main_bg.png"
import { WallpaperImageRes, WallpaperThemeRes } from "@/api/model"
import { BizError } from "@/models/biz_model"
import { UserUsecase } from "@/usecases/user"
import { WallpaperUseCase } from "@/usecases/wallpaper"
import { LoginWallpapeCache, WallpaperListCache } from "@/utils/storage"
import {
  Box,
  Button,
  Flex,
  Image,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react"
import React, { useEffect, useMemo, useState } from "react"

type WallpaperTarget = "login" | "windows"

interface WallpaperImageItem extends WallpaperImageRes {
  themeTitle: string
  isDefault?: boolean
}

const getWallpaperThumbnailSrc = (
  wallpaper: Pick<WallpaperImageRes, "img" | "img_thumb"> | null | undefined
) => {
  return (
    wallpaper?.img_thumb?.trim() || wallpaper?.img?.trim() || loginDefaultBg
  )
}

const getWallpaperThumbnailSrcWithoutFallback = (
  wallpaper: Pick<WallpaperImageRes, "img" | "img_thumb"> | null | undefined
) => {
  return wallpaper?.img_thumb?.trim() || wallpaper?.img?.trim() || ""
}

const getWallpaperOriginalSrc = (
  wallpaper: Pick<WallpaperImageRes, "img"> | null | undefined
) => {
  return wallpaper?.img?.trim() || ""
}

const preloadImage = async (src: string): Promise<void> => {
  if (src.trim() === "") {
    return
  }

  await new Promise<void>((resolve) => {
    const preloader = new window.Image()
    const timer = window.setTimeout(() => {
      preloader.onload = null
      preloader.onerror = null
      resolve()
    }, 1500)

    const finalize = () => {
      window.clearTimeout(timer)
      preloader.onload = null
      preloader.onerror = null
      resolve()
    }

    preloader.onload = finalize
    preloader.onerror = finalize
    preloader.src = src

    if (preloader.complete) {
      finalize()
    }
  })
}

const preloadVisibleWallpaperThumbnails = async (
  list: WallpaperThemeRes[]
): Promise<void> => {
  const previewUrls = list.flatMap((theme) =>
    theme.images
      .slice(0, 5)
      .map((image) => getWallpaperThumbnailSrcWithoutFallback(image))
      .filter((src) => src !== "")
  )

  await Promise.all(previewUrls.map((src) => preloadImage(src)))
}

const getWallpaperItems = (list: WallpaperThemeRes[]): WallpaperImageItem[] => {
  return list.flatMap((theme) =>
    theme.images.map((image) => ({
      ...image,
      themeTitle: theme.title,
    }))
  )
}

interface WallpaperSettingModalProps {
  isOpen: boolean
  onClose: () => void
}

const DEFAULT_LOGIN_WALLPAPER: WallpaperImageItem = {
  img: loginDefaultBg,
  img_thumb: loginDefaultBg,
  resource_id: "default-login-wallpaper",
  themeTitle: "默认",
  isDefault: true,
}

export const WallpaperSetting: React.FC<WallpaperSettingModalProps> = ({
  isOpen,
  onClose,
}) => {
  const toast = useToast()
  const [activeTarget, setActiveTarget] = useState<WallpaperTarget>("login")
  const [loading, setLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [themes, setThemes] = useState<WallpaperThemeRes[]>([])
  const [expandedThemes, setExpandedThemes] = useState<Record<string, boolean>>(
    {}
  )
  const [loginWallpaper, setLoginWallpaper] = useState<WallpaperImageItem>(
    DEFAULT_LOGIN_WALLPAPER
  )
  const [windowsWallpaper, setWindowsWallpaper] =
    useState<WallpaperImageItem | null>(null)
  const [currentLoginBg, setCurrentLoginBg] = useState<string | null>(
    LoginWallpapeCache.getLoginWallpaperSource()
  )
  const [currentDeskWallpaper, setCurrentDeskWallpaper] = useState<
    string | null
  >(WallpaperUseCase.getCurrentDesktopWallpaper())

  const syncWallpaperState = (list: WallpaperThemeRes[]) => {
    const cachedBg = LoginWallpapeCache.getLoginWallpaperSource()
    const cachedDesktopWallpaper = WallpaperUseCase.getCurrentDesktopWallpaper()
    const allImages = getWallpaperItems(list)
    const matchedLoginWallpaper =
      allImages.find((item) => item.img === cachedBg) ??
      allImages.find((item) => item.resource_id === cachedBg)
    const matchedDesktopWallpaper =
      allImages.find((item) => item.img === cachedDesktopWallpaper) ?? null

    setThemes(list)
    setCurrentLoginBg(cachedBg)
    setCurrentDeskWallpaper(cachedDesktopWallpaper)
    setLoginWallpaper(matchedLoginWallpaper ?? DEFAULT_LOGIN_WALLPAPER)
    setWindowsWallpaper(matchedDesktopWallpaper ?? null)
    setExpandedThemes({})
  }

  useEffect(() => {
    if (!isOpen) {
      return
    }

    let active = true

    const init = async () => {
      const cachedWallpaperList = WallpaperListCache.getWallpaperList()
      const staleWallpaperList = WallpaperListCache.getWallpaperListStale()
      const cachedList = cachedWallpaperList?.list ?? []
      const staleList = staleWallpaperList?.list ?? []

      if (cachedList.length > 0) {
        syncWallpaperState(cachedList)
        void preloadVisibleWallpaperThumbnails(cachedList)
        setLoading(false)
      } else {
        setLoading(true)
      }

      const res = await UserUsecase.getWallpaperList()
      if (!active) {
        return
      }

      if (!(res instanceof BizError)) {
        const list = res.unwrap()
        WallpaperListCache.setWallpaperList(list)
        syncWallpaperState(list)
        void preloadVisibleWallpaperThumbnails(list)
      } else {
        if (cachedList.length === 0 && staleList.length > 0) {
          syncWallpaperState(staleList)
          void preloadVisibleWallpaperThumbnails(staleList)
        } else if (cachedList.length === 0) {
          toast({
            title: res.displayErrorMsg,
            status: "error",
            duration: 2000,
            isClosable: true,
          })
        }
      }

      setLoading(false)
    }

    void init()

    return () => {
      active = false
    }
  }, [isOpen, toast])

  const themedList = useMemo(() => {
    const remoteThemes = themes.map((theme) => ({
      ...theme,
      images: theme.images.map((image) => ({
        ...image,
        themeTitle: theme.title,
      })),
    }))

    if (activeTarget === "login") {
      return [
        {
          theme_id: "default-theme",
          title: "默认",
          images: [DEFAULT_LOGIN_WALLPAPER],
        },
        ...remoteThemes,
      ]
    }

    return remoteThemes
  }, [activeTarget, themes])

  const currentWallpaper =
    activeTarget === "login" ? loginWallpaper : windowsWallpaper

  const selectWallpaper = (wallpaper: WallpaperImageItem) => {
    if (activeTarget === "login") {
      setLoginWallpaper(wallpaper)
      return
    }

    setWindowsWallpaper(wallpaper)
  }

  const handleConfirm = async () => {
    setIsSaving(true)
    try {
      LoginWallpapeCache.setLoginWallpaper({
        src: getWallpaperOriginalSrc(loginWallpaper),
        thumbSrc:
          getWallpaperThumbnailSrcWithoutFallback(loginWallpaper) ||
          getWallpaperOriginalSrc(loginWallpaper),
      })
      setCurrentLoginBg(getWallpaperOriginalSrc(loginWallpaper))

      if (windowsWallpaper !== null) {
        const res = await WallpaperUseCase.setDesktopWallpaper(windowsWallpaper)
        if (res instanceof BizError) {
          toast({
            title: res.displayErrorMsg,
            status: "error",
            duration: 2000,
            isClosable: true,
          })
        } else {
          setCurrentDeskWallpaper(windowsWallpaper.img)
        }
      }
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) {
    return <></>
  }

  return (
    <Flex
      id="full-screen-unvideo-element-tag"
      width="100vw"
      height="100vh"
      position="fixed"
      left={0}
      top={0}
      backgroundColor="rgba(0, 0, 0, 0.5)"
      justifyContent="center"
      alignItems="center"
      zIndex={10000}
    >
      <Flex
        id="full-screen-unvideo-element-tag"
        direction="column"
        width="860px"
        maxHeight="85vh"
        background="#5a5a5c"
        border="1px solid #ffffff29"
        borderRadius="14px"
        overflow="hidden"
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          padding="12px 14px 16px"
        >
          <Text color="white" fontSize="18px" fontWeight={600}>
            壁纸设置
          </Text>
          <Button
            variant="unstyled"
            color="white"
            minWidth="auto"
            height="auto"
            fontSize="20px"
            padding="0"
            onClick={onClose}
          >
            ×
          </Button>
        </Flex>

        <Flex direction="column" padding="0 18px 16px" gap="14px" minHeight="0">
          <Flex alignItems="flex-end" justifyContent="space-between" gap="12px">
            <Flex gap="12px" flex="1" minWidth="0">
              <WallpaperTargetPreview
                title="登录页壁纸"
                wallpaper={loginWallpaper}
                active={activeTarget === "login"}
                useDefaultFallback
                onClick={() => setActiveTarget("login")}
              />
              <WallpaperTargetPreview
                title="Windows桌面壁纸"
                wallpaper={windowsWallpaper}
                active={activeTarget === "windows"}
                onClick={() => setActiveTarget("windows")}
              />
            </Flex>
            <Flex gap="12px" flex="1" justifyContent="flex-end">
              <Button
                size="xs"
                background="#1677ff"
                color="white"
                _hover={{ background: "#0f67df" }}
                isDisabled={isSaving}
                alignSelf="flex-end"
                minWidth="46px"
                height="24px"
                fontSize="12px"
                padding="0 10px"
                onClick={() => {
                  void handleConfirm()
                }}
              >
                {isSaving ? "设置中..." : "设置"}
              </Button>
            </Flex>
          </Flex>

          <Box height="1px" background="#7a7a7a" width="100%" />

          <Box overflowY="auto" flex={1} minHeight="0" paddingRight="2px">
            {loading ? (
              <Flex
                justifyContent="center"
                alignItems="center"
                minHeight="180px"
              >
                <LoadingImagePlaceholder height="180px" />
              </Flex>
            ) : (
              themedList.map((theme) => {
                const isExpanded = expandedThemes[theme.theme_id] ?? false
                const visibleImages = isExpanded
                  ? theme.images
                  : theme.images.slice(0, 5)
                return (
                  <Box key={theme.theme_id} marginBottom="14px">
                    <Flex
                      justifyContent="space-between"
                      alignItems="center"
                      marginBottom="10px"
                    >
                      <Text color="white" fontSize="14px" fontWeight={600}>
                        {theme.title}
                      </Text>
                      {theme.images.length > 5 ? (
                        <Button
                          size="xs"
                          variant="unstyled"
                          color="#ffffffd9"
                          onClick={() =>
                            setExpandedThemes((prev) => ({
                              ...prev,
                              [theme.theme_id]: !isExpanded,
                            }))
                          }
                        >
                          {isExpanded
                            ? "收起"
                            : `展开 (${theme.images.length})`}
                        </Button>
                      ) : (
                        <Text color="#ffffff8f" fontSize="12px">
                          共 {theme.images.length} 张
                        </Text>
                      )}
                    </Flex>
                    <Flex wrap="wrap" gap="10px">
                      {visibleImages.map((image) => {
                        const selected = currentWallpaper?.img === image.img
                        const isUsing =
                          activeTarget === "login"
                            ? currentLoginBg === image.img
                            : currentDeskWallpaper === image.img
                        return (
                          <Box
                            key={image.resource_id}
                            width="152px"
                            position="relative"
                            cursor="pointer"
                            border={
                              selected
                                ? "2px solid #1677ff"
                                : "1px solid transparent"
                            }
                            borderRadius="10px"
                            onClick={() => selectWallpaper(image)}
                          >
                            {isUsing ? (
                              <Box
                                position="absolute"
                                top="6px"
                                right="6px"
                                zIndex={2}
                                background="rgba(0, 0, 0, 0.8)"
                                color="white"
                                fontSize="11px"
                                lineHeight="16px"
                                padding="2px 6px"
                                borderRadius="6px"
                              >
                                使用中
                              </Box>
                            ) : null}
                            <AsyncImage
                              src={getWallpaperThumbnailSrc(image)}
                              width="100%"
                              height="88px"
                              objectFit="cover"
                              borderRadius="8px"
                            />
                          </Box>
                        )
                      })}
                    </Flex>
                  </Box>
                )
              })
            )}
          </Box>
        </Flex>
      </Flex>
    </Flex>
  )
}

const WallpaperTargetPreview: React.FC<{
  title: string
  wallpaper: WallpaperImageItem | null
  active: boolean
  useDefaultFallback?: boolean
  onClick: () => void
}> = ({ title, wallpaper, active, useDefaultFallback = false, onClick }) => {
  const previewSrc = useDefaultFallback
    ? getWallpaperThumbnailSrc(wallpaper)
    : getWallpaperThumbnailSrcWithoutFallback(wallpaper)

  return (
    <Flex
      flex="1"
      maxWidth="calc(50% - 6px)"
      minWidth="0"
      direction="column"
      alignItems="center"
      cursor="pointer"
      onClick={onClick}
    >
      <Box width="100%" borderRadius="12px">
        {previewSrc === "" ? (
          <Flex
            width="100%"
            height="104px"
            borderRadius="8px"
            background="#3d3d3d"
            justifyContent="center"
            alignItems="center"
          >
            <Text color="#ffffff8f" fontSize="12px">
              暂无选中图片
            </Text>
          </Flex>
        ) : (
          <AsyncImage
            src={previewSrc}
            width="100%"
            height="104px"
            objectFit="cover"
            borderRadius="8px"
          />
        )}
      </Box>
      <Flex
        alignItems="center"
        justifyContent="center"
        gap="8px"
        marginTop="8px"
        width="100%"
      >
        <Box
          width="14px"
          height="14px"
          borderRadius="50%"
          border="1px solid #ffffffd9"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {active ? (
            <Box
              width="8px"
              height="8px"
              borderRadius="50%"
              background="#1677ff"
            />
          ) : null}
        </Box>
        <Text color="white" fontSize="13px" textAlign="center">
          {title}
        </Text>
      </Flex>
    </Flex>
  )
}

const AsyncImage: React.FC<{
  src: string
  width: string
  height: string
  objectFit: "cover"
  borderRadius: string
}> = ({ src, width, height, objectFit, borderRadius }) => {
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    setLoaded(false)

    if (src.trim() === "") {
      setLoaded(true)
      return
    }

    let cancelled = false
    const preloader = new window.Image()
    const finalize = () => {
      if (!cancelled) {
        setLoaded(true)
      }
    }

    preloader.onload = finalize
    preloader.onerror = finalize
    preloader.src = src

    if (preloader.complete) {
      finalize()
    }

    return () => {
      cancelled = true
      preloader.onload = null
      preloader.onerror = null
    }
  }, [src])

  return (
    <Box position="relative" width={width} height={height}>
      {!loaded ? <LoadingImagePlaceholder height={height} /> : null}
      <Image
        src={src}
        width={width}
        height={height}
        objectFit={objectFit}
        borderRadius={borderRadius}
        position="absolute"
        top="0"
        left="0"
        opacity={loaded ? 1 : 0}
        transition="opacity 0.2s ease"
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </Box>
  )
}

const LoadingImagePlaceholder: React.FC<{ height: string }> = ({ height }) => {
  return (
    <Flex
      width="100%"
      height={height}
      borderRadius="8px"
      background="#3d3d3d"
      justifyContent="center"
      alignItems="center"
      direction="column"
      gap="8px"
    >
      <Spinner color="white" size="sm" />
      <Text color="#ffffffd9" fontSize="12px">
        图片加载中
      </Text>
    </Flex>
  )
}
